// Pivot measures: what each cell holds. Count (the default) tallies reports;
// the duration measures summarize how long reports took between two statuses.
//
// A report's status JSON records when it ENTERED each status:
//   status.current + status.assigned       -> the status it is in now
//   status.history[] { status, assigned }  -> every earlier one
// (djaguar's build_history_status moves the current status into the history on
// every change). Timestamps are UTC strings, "YYYY/MM/DD HH:MM:SS".
//
// Rules for a duration `from -> to`:
//   - start = FIRST entry into `from`; end = FIRST entry into `to` at or after
//     that start. Reports that go back (pending -> new) or repeat a status
//     (process -> process) therefore measure from the first time the stage began.
//   - closure is special: it only counts reports whose CURRENT status is closed,
//     and ends at that (current) closing time — a reopened case is not closed.
//   - an incomplete stage (skipped, or not reached yet) yields null and is left
//     out of the stats, never counted as 0.

export const MEASURE_COUNT = "count";

export const DURATION_MEASURES = [
  { key: "closure", from: "new", to: "closed", currentOnly: true },
  { key: "attention", from: "new", to: "pending" },
  { key: "preparation", from: "pending", to: "process" },
  { key: "investigation", from: "process", to: "finish" },
  { key: "resolution", from: "finish", to: "closed" },
];

export const MEASURE_KEYS = [MEASURE_COUNT, ...DURATION_MEASURES.map((m) => m.key)];

export const STATS = ["avg", "median", "min", "max", "p90"];
export const DEFAULT_STAT = "avg";

export const isDurationMeasure = (key) => DURATION_MEASURES.some((m) => m.key === key);

// Extra EQL fields a duration measure needs (json paths come back keyed by the
// literal path, like the status dimension's "status.current"). `id` lets the
// pivot count a report once even when a multi-value catalog splits it into
// several records.
export const DURATION_FIELDS = ["id", "status.current", "status.assigned", "status.history"];

// Record keys flattenRows adds for the pivot engine.
export const RECORD_ID_FIELD = "__rid";
export const durationField = (key) => `__dur_${key}`;

const DAY_MS = 24 * 60 * 60 * 1000;

const DATE_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/;

/**
 * Parses a status timestamp as UTC. Done by hand on purpose: `new Date()` reads
 * "2026/09/10 15:02:11" as LOCAL time, shifting every duration by the offset.
 * @returns {number|null} epoch ms, or null when unparseable
 */
export const parseStatusDate = (value) => {
  if (typeof value !== "string") return null;
  const m = DATE_RE.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h = 0, mi = 0, s = 0] = m;
  const ms = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
  return Number.isNaN(ms) ? null : ms;
};

const lower = (s) => (s == null ? "" : String(s).toLowerCase());

/** Every status entry (history + current) with a parseable time, oldest first. */
const statusEntries = (status) => {
  const entries = [];
  (Array.isArray(status?.history) ? status.history : []).forEach((h) => {
    const at = parseStatusDate(h?.assigned);
    if (h?.status && at != null) entries.push({ status: lower(h.status), at });
  });
  const currentAt = parseStatusDate(status?.assigned);
  if (status?.current && currentAt != null) {
    entries.push({ status: lower(status.current), at: currentAt });
  }
  return entries.sort((a, b) => a.at - b.at);
};

/**
 * Days a report spent on one measure's stage, or null when the stage is
 * incomplete. See the rules at the top of this file.
 * @param {{current?: string, assigned?: string, history?: Array}} status
 * @param {{from: string, to: string, currentOnly?: boolean}} measure
 * @returns {number|null}
 */
export const durationDays = (status, measure) => {
  const entries = statusEntries(status);
  const start = entries.find((e) => e.status === measure.from);
  if (!start) return null;

  let end;
  if (measure.currentOnly) {
    if (lower(status?.current) !== measure.to) return null;
    end = parseStatusDate(status?.assigned);
  } else {
    end = entries.find((e) => e.status === measure.to && e.at >= start.at)?.at;
  }
  if (end == null || end < start.at) return null;
  return (end - start.at) / DAY_MS;
};

/** Status JSON rebuilt from a raw EQL row fetched with DURATION_FIELDS. */
const statusOfRow = (row) => ({
  current: row?.["status.current"],
  assigned: row?.["status.assigned"],
  history: row?.["status.history"],
});

/** All duration fields for one raw EQL row: { __dur_<key>: days|null }. */
export const durationsOfRow = (row) => {
  const status = statusOfRow(row);
  return Object.fromEntries(
    DURATION_MEASURES.map((m) => [durationField(m.key), durationDays(status, m)])
  );
};

/** Linear-interpolated percentile (Excel's PERCENTILE.INC) of sorted values. */
const percentile = (sorted, p) => {
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
};

/**
 * @param {number[]} values
 * @param {string} stat - one of STATS
 * @returns {number|null} null for an empty list
 */
export const computeStat = (values, stat = DEFAULT_STAT) => {
  if (!values?.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  switch (stat) {
    case "min":
      return sorted[0];
    case "max":
      return sorted[sorted.length - 1];
    case "median":
      return percentile(sorted, 0.5);
    case "p90":
      return percentile(sorted, 0.9);
    default:
      return sorted.reduce((sum, v) => sum + v, 0) / sorted.length;
  }
};

/** Days rounded to one decimal, the precision shown everywhere. */
export const roundDays = (v) => (v == null ? null : Math.round(v * 10) / 10);

/** "12.3 d"; empty for a cell with no data. */
export const formatDays = (v) =>
  v == null
    ? ""
    : `${v.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} d`;

/** Header for the value column, e.g. "Promedio · Tiempo de cierre (días)". */
export const measureLabel = (t, measure) =>
  measure
    ? `${t(`builder.stat_${measure.stat}`)} · ${t(`builder.measure_${measure.key}`)} (${t(
        "builder.days"
      )})`
    : t("builder.count");
